import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { BooleanCapabilityKey } from '../plans/types';
import { CAPABILITY_UPGRADE_COPY } from '../plans/capabilityCopy';

interface UpgradePromptProps {
  capability: BooleanCapabilityKey;
  title?: string;
  description?: string;
  className?: string;
  hideTitle?: boolean;
  collapsible?: boolean;
  collapseStorageKey?: string;
}

function readCollapsedState(storageKey: string): boolean {
  try {
    return sessionStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedState(storageKey: string, collapsed: boolean) {
  try {
    sessionStorage.setItem(storageKey, collapsed ? 'true' : 'false');
  } catch {
    // ignore
  }
}

export default function UpgradePrompt({
  capability,
  title,
  description,
  className = '',
  hideTitle = false,
  collapsible = false,
  collapseStorageKey,
}: UpgradePromptProps) {
  const copy = CAPABILITY_UPGRADE_COPY[capability];
  const heading = title || copy.title;
  const body = description || copy.description;
  const storageKey =
    collapseStorageKey ?? `upgradePrompt:${capability}:collapsed`;

  const [collapsed, setCollapsed] = useState(() =>
    collapsible ? readCollapsedState(storageKey) : false
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsedState(storageKey, next);
      return next;
    });
  };

  return (
    <div
      className={`w-full rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 p-5 ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        {!hideTitle && (
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {heading}
          </h3>
        )}
        {collapsible && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand upgrade details' : 'Minimize upgrade details'}
            className="flex-shrink-0 rounded-md p-1 text-gray-500 hover:bg-indigo-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-indigo-900/40 dark:hover:text-gray-200"
          >
            {collapsed ? (
              <ChevronDownIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
            ) : (
              <ChevronUpIcon className="h-5 w-5 pointer-events-none" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {collapsed ? (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <Link
            to="/app/settings/billing"
            className="inline-flex w-full sm:w-auto items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            View upgrade options
          </Link>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="text-sm font-medium text-indigo-700 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            Show details
          </button>
        </div>
      ) : (
        <>
          <p className={`text-sm text-gray-600 dark:text-gray-300 ${hideTitle ? '' : 'mt-2'}`}>
            {body}
          </p>
          <div className="mt-4">
            <Link
              to="/app/settings/billing"
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              View upgrade options
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
